import { supabase } from '../lib/supabase';

/**
 * Service for managing squads
 */
export const squadService = {
  /**
   * Get user's squads
   */
  async getUserSquads(userId) {
    const { data, error } = await supabase
      ?.from('squad_members')?.select(`*,squad:squads(id,name,description,icon,color,creator_id,created_at)`)?.eq('user_id', userId)?.order('joined_at', { ascending: false });

    if (error) throw error;
    return data?.map(item => item?.squad) || [];
  },

  /**
   * Get squad members
   */
  async getSquadMembers(squadId) {
    const { data, error } = await supabase
      ?.from('squad_members')?.select(`*,user:user_profiles(id, full_name, avatar_url, total_points, current_streak)`)?.eq('squad_id', squadId)?.order('joined_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Create a new squad
   */
  async createSquad(userId, squadData) {
    // First verify user profile exists
    const { data: profile, error: profileError } = await supabase
      ?.from('user_profiles')?.select('id')?.eq('id', userId)
      ?.single();

    if (profileError || !profile) {
      throw new Error('User profile not found. Please ensure your profile is set up correctly.');
    }

    const { data: squad, error: squadError } = await supabase
      ?.from('squads')
      ?.insert({
        name: squadData?.name,
        description: squadData?.description,
        creator_id: userId,
        icon: squadData?.icon || 'Users',color: squadData?.color || 'primary'
      })
      ?.select()
      ?.single();

    if (squadError) throw squadError;

    // Add creator as admin member
    const { error: memberError } = await supabase
      ?.from('squad_members')
      ?.insert({
        squad_id: squad?.id,
        user_id: userId,
        role: 'admin'
      });

    if (memberError) throw memberError;
    return squad;
  },

  /**
   * Add member to squad
   */
  async addSquadMember(squadId, userId, role = 'member') {
    const { data, error } = await supabase
      ?.from('squad_members')
      ?.insert({
        squad_id: squadId,
        user_id: userId,
        role: role
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Create squad invitation
   */
  async createSquadInvitation(squadId, inviterId, inviteeId) {
    const { data, error } = await supabase
      ?.from('squad_invitations')
      ?.insert({
        squad_id: squadId,
        inviter_id: inviterId,
        invitee_id: inviteeId,
        status: 'pending'
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Get pending squad invitations for a user
   */
  async getPendingSquadInvitations(userId) {
    const { data, error } = await supabase
      ?.from('squad_invitations')?.select(`*,squad:squads(id, name, description, icon, color),inviter:user_profiles!squad_invitations_inviter_id_fkey(id, full_name, avatar_url)`)?.eq('invitee_id', userId)?.eq('status', 'pending')?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Accept squad invitation
   */
  async acceptSquadInvitation(invitationId, userId) {
    // Get invitation details
    const { data: invitation, error: invError } = await supabase
      ?.from('squad_invitations')?.select('squad_id')?.eq('id', invitationId)?.eq('invitee_id', userId)
      ?.single();

    if (invError) throw invError;

    // Add user to squad
    await this.addSquadMember(invitation?.squad_id, userId, 'member');

    // Update invitation status
    const { data, error } = await supabase
      ?.from('squad_invitations')
      ?.update({ status: 'accepted' })
      ?.eq('id', invitationId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Decline squad invitation
   */
  async declineSquadInvitation(invitationId, userId) {
    const { data, error } = await supabase
      ?.from('squad_invitations')
      ?.update({ status: 'declined' })
      ?.eq('id', invitationId)?.eq('invitee_id', userId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  }
};

/**
 * Service for managing wellness challenges
 */
export const challengeService = {
  /**
   * Get all active challenges
   */
  async getActiveChallenges() {
    const { data, error } = await supabase
      ?.from('wellness_challenges')
      ?.select('*')
      ?.eq('is_active', true)
      ?.gte('end_date', new Date()?.toISOString()?.split('T')?.[0])
      ?.order('start_date', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get challenge participants with user details
   */
  async getChallengeParticipants(challengeId) {
    const { data, error } = await supabase
      ?.from('challenge_participants')
      ?.select(`
        *,
        user_profiles!challenge_participants_user_id_fkey(
          id,
          full_name,
          avatar_url,
          total_points
        )
      `)
      ?.eq('challenge_id', challengeId)
      ?.order('current_progress', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Join a challenge
   */
  async joinChallenge(userId, challengeId) {
    const { data, error } = await supabase
      ?.from('challenge_participants')
      ?.insert({
        user_id: userId,
        challenge_id: challengeId,
        current_progress: 0,
        is_completed: false
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Update challenge progress
   */
  async updateChallengeProgress(participantId, progress, isCompleted = false) {
    const updateData = {
      current_progress: progress,
      is_completed: isCompleted
    };

    if (isCompleted) {
      updateData.completed_at = new Date()?.toISOString();
    }

    const { data, error } = await supabase
      ?.from('challenge_participants')
      ?.update(updateData)
      ?.eq('id', participantId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Get user's challenge participation
   */
  async getUserChallenges(userId) {
    const { data, error } = await supabase
      ?.from('challenge_participants')
      ?.select(`
        *,
        wellness_challenges(*)
      `)
      ?.eq('user_id', userId)
      ?.order('joined_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Create custom challenge with invitations
   */
  async createCustomChallenge(creatorId, challengeData) {
    const startDate = new Date();
    const endDate = new Date();
    endDate?.setDate(endDate?.getDate() + challengeData?.durationDays);

    // Create the challenge
    const { data: challenge, error: challengeError } = await supabase
      ?.from('wellness_challenges')
      ?.insert({
        title: challengeData?.title,
        description: challengeData?.description,
        challenge_type: challengeData?.challengeType,
        target_value: challengeData?.targetValue,
        unit: challengeData?.unit,
        start_date: startDate?.toISOString()?.split('T')?.[0],
        end_date: endDate?.toISOString()?.split('T')?.[0],
        icon: challengeData?.icon,
        color: challengeData?.color,
        is_active: true
      })
      ?.select()
      ?.single();

    if (challengeError) throw challengeError;

    // Auto-join creator
    await this.joinChallenge(creatorId, challenge?.id);

    // Handle invitations based on type
    if (challengeData?.invitationType === 'squad' && challengeData?.squadId) {
      // Get squad members and invite them
      const members = await squadService?.getSquadMembers(challengeData?.squadId);
      const invitePromises = members
        ?.filter(m => m?.user_id !== creatorId)
        ?.map(m => this.joinChallenge(m?.user_id, challenge?.id));
      await Promise?.allSettled(invitePromises);
    } else if (challengeData?.invitationType === 'username' && challengeData?.userIds?.length > 0) {
      // Invite selected users
      const invitePromises = challengeData?.userIds?.map(userId => 
        this.joinChallenge(userId, challenge?.id)
      );
      await Promise?.allSettled(invitePromises);
    } else if (challengeData?.invitationType === 'email' && challengeData?.inviteEmail) {
      // Search user by email and invite
      const { data: users } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('email', challengeData?.inviteEmail)
        ?.limit(1);
      
      if (users && users?.length > 0) {
        await this.joinChallenge(users?.[0]?.id, challenge?.id);
      }
    }

    return challenge;
  }
};

/**
 * Service for managing friendships
 */
export const friendshipService = {
  /**
   * Get user's friends
   */
  async getFriends(userId) {
    const { data, error } = await supabase
      ?.from('friendships')?.select(`*,user_profiles!friendships_friend_id_fkey(id,full_name,avatar_url,total_points,current_streak)`)?.eq('user_id', userId)?.eq('status', 'accepted')?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get pending friend requests (received)
   */
  async getPendingRequests(userId) {
    const { data, error } = await supabase
      ?.from('friendships')?.select(`*,user_profiles!friendships_user_id_fkey(id,full_name,avatar_url,total_points,current_streak)`)?.eq('friend_id', userId)?.eq('status', 'pending')?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get sent friend requests (pending)
   */
  async getSentRequests(userId) {
    const { data, error } = await supabase
      ?.from('friendships')?.select(`*,user_profiles!friendships_friend_id_fkey(id,full_name,avatar_url,total_points)`)?.eq('user_id', userId)?.eq('status', 'pending')?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get friend leaderboard
   */
  async getFriendLeaderboard(userId, metric = 'total_points') {
    const friends = await this.getFriends(userId);
    const friendIds = friends?.map(f => f?.friend_id) || [];
    
    // Include current user in leaderboard
    friendIds?.push(userId);

    const { data, error } = await supabase
      ?.from('user_profiles')?.select('id, full_name, avatar_url, total_points, current_streak, duels_won, duels_completed')?.in('id', friendIds)
      ?.order(metric, { ascending: false })
      ?.limit(10);

    if (error) throw error;
    return data || [];
  },

  /**
   * Get recent workout activity from friends
   */
  async getFriendWorkoutActivity(userId, limit = 15) {
    try {
      // Get user's accepted friends
      const { data: friendships, error: friendError } = await supabase
        ?.from('friendships')
        ?.select('friend_id')
        ?.eq('user_id', userId)
        ?.eq('status', 'accepted');

      if (friendError) throw friendError;

      const friendIds = friendships?.map(f => f?.friend_id) || [];
      if (friendIds?.length === 0) return [];

      // Fetch recent workout logs from friends
      const { data: workouts, error: workoutsError } = await supabase
        ?.from('workout_logs')
        ?.select(`
          id,
          user_id,
          workout_date,
          workout_type,
          exercise_name,
          duration_minutes,
          calories_burned,
          steps,
          intensity_level,
          created_at
        `)
        ?.in('user_id', friendIds)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (workoutsError) throw workoutsError;

      // Fetch user profiles separately
      const { data: profiles } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, avatar_url')
        ?.in('id', friendIds);

      const profileMap = {};
      (profiles || [])?.forEach(p => { profileMap[p.id] = p; });

      return (workouts || [])?.map(w => ({
        id: w?.id,
        type: 'workout',
        userId: w?.user_id,
        user: profileMap?.[w?.user_id] || { full_name: 'Unknown', avatar_url: null },
        workoutType: w?.workout_type,
        exerciseName: w?.exercise_name,
        durationMinutes: w?.duration_minutes,
        caloriesBurned: w?.calories_burned,
        steps: w?.steps,
        intensityLevel: w?.intensity_level,
        workoutDate: w?.workout_date,
        createdAt: w?.created_at
      }));
    } catch (error) {
      console.error('Error fetching friend workout activity:', error);
      return [];
    }
  },

  /**
   * Send friend request (status: pending)
   */
  async sendFriendRequest(userId, friendId) {
    const { data, error } = await supabase
      ?.from('friendships')
      ?.insert({
        user_id: userId,
        friend_id: friendId,
        status: 'pending'
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Accept friend request
   */
  async acceptFriendRequest(friendshipId, userId) {
    // Update the friendship status to accepted
    const { data: friendship, error: updateError } = await supabase
      ?.from('friendships')
      ?.update({ status: 'accepted' })
      ?.eq('id', friendshipId)?.eq('friend_id', userId)
      ?.select()
      ?.single();

    if (updateError) throw updateError;

    // Create reciprocal friendship
    const { error: reciprocalError } = await supabase
      ?.from('friendships')
      ?.insert({
        user_id: userId,
        friend_id: friendship?.user_id,
        status: 'accepted'
      });

    if (reciprocalError) throw reciprocalError;

    // Send email notification to the original requester
    // Remove this block - socialEmailService is not imported/defined
    // if (friendship?.user_id) {
    //   socialEmailService?.sendFriendAcceptedEmail(friendship?.user_id, userId)?.catch(err => console.error('Failed to send friend accepted email:', err));
    // }

    return friendship;
  },

  /**
   * Reject/cancel friend request
   */
  async rejectFriendRequest(friendshipId, userId) {
    const { data, error } = await supabase
      ?.from('friendships')?.delete()?.eq('id', friendshipId)?.eq('friend_id', userId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Add friend (legacy - now sends pending request)
   */
  async addFriend(userId, friendId) {
    return this.sendFriendRequest(userId, friendId);
  },

  /**
   * Search users for friend discovery
   */
  async searchUsers(searchTerm, currentUserId) {
    const { data, error } = await supabase
      ?.from('user_profiles')?.select('id, full_name, email, avatar_url, total_points')?.neq('id', currentUserId)
      ?.or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
      ?.limit(10);

    if (error) throw error;
    return data || [];
  }
};

/**
 * Service for managing milestones
 */
export const milestoneService = {
  /**
   * Get recent milestones from friends
   */
  async getFriendMilestones(userId, limit = 20) {
    const friends = await friendshipService?.getFriends(userId);
    const friendIds = friends?.map(f => f?.friend_id) || [];
    friendIds?.push(userId);

    const { data, error } = await supabase
      ?.from('milestones')
      ?.select(`
        *,
        user_profiles(
          id,
          full_name,
          avatar_url
        )
      `)
      ?.in('user_id', friendIds)
      ?.order('created_at', { ascending: false })
      ?.limit(limit);

    if (error) throw error;
    return data || [];
  },

  /**
   * Create milestone
   */
  async createMilestone(userId, milestoneData) {
    const { data, error } = await supabase
      ?.from('milestones')
      ?.insert({
        user_id: userId,
        milestone_type: milestoneData?.type,
        title: milestoneData?.title,
        description: milestoneData?.description,
        icon: milestoneData?.icon,
        color: milestoneData?.color,
        points_earned: milestoneData?.pointsEarned || 0
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  }
};

/**
 * Service for managing achievement badges
 */
export const achievementService = {
  /**
   * Get user's achievement badges
   */
  async getUserBadges(userId) {
    const { data, error } = await supabase
      ?.from('achievement_badges')?.select('*')?.eq('user_id', userId)?.order('earned_date', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Award badge to user
   */
  async awardBadge(userId, badgeData) {
    const { data, error } = await supabase
      ?.from('achievement_badges')
      ?.insert({
        user_id: userId,
        badge_name: badgeData?.name,
        badge_description: badgeData?.description,
        badge_icon: badgeData?.icon,
        badge_color: badgeData?.color,
        category: badgeData?.category
      })
      ?.select()
      ?.single();

    if (error) throw error;
    
    // Send email notification for badge achievement
    // Remove this block - service not imported/defined
    // if (data) {
    //   socialEmailService?.sendAchievementMilestoneEmail(userId, {
    //     title: `Badge Earned: ${badgeData?.name}`,
    //     description: badgeData?.description,
    //     icon: badgeData?.icon || '🏆',
    //     pointsEarned: 50,
    //     category: badgeData?.category
    //   })?.catch(err => console.error('Failed to send badge achievement email:', err));
    // }
    
    return data;
  },

  /**
   * Get badge statistics by category
   */
  async getBadgeStats(userId) {
    const { data, error } = await supabase
      ?.from('achievement_badges')?.select('category')?.eq('user_id', userId);

    if (error) throw error;

    const stats = {
      activity: 0,
      nutrition: 0,
      sleep: 0,
      social: 0,
      consistency: 0,
      total: data?.length || 0
    };

    data?.forEach(badge => {
      if (stats?.[badge?.category] !== undefined) {
        stats[badge?.category]++;
      }
    });

    return stats;
  }
};

/**
 * Subscribe to real-time updates
 */
export const socialRealtimeService = {
  /**
   * Subscribe to challenge participant updates
   */
  subscribeToChallengeUpdates(challengeId, callback) {
    const channel = supabase
      ?.channel(`challenge_${challengeId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'challenge_participants',
          filter: `challenge_id=eq.${challengeId}`
        },
        callback
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to milestone updates
   */
  subscribeToMilestones(userId, callback) {
    const channel = supabase
      ?.channel('milestones_feed')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'milestones'
        },
        callback
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to badge updates
   */
  subscribeToBadges(userId, callback) {
    const channel = supabase
      ?.channel('badges_updates')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'achievement_badges',
          filter: `user_id=eq.${userId}`
        },
        callback
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to duel updates for a specific user
   * Listens to duels where user is creator or opponent
   */
  subscribeToDuelUpdates(userId, callback) {
    const channel = supabase
      ?.channel('duels_realtime')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duels',
          filter: `creator_id=eq.${userId}`
        },
        callback
      )
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duels',
          filter: `opponent_id=eq.${userId}`
        },
        callback
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to friendship updates (friend requests and accepts)
   * Listens to friendships where user is requester or receiver
   */
  subscribeToFriendshipUpdates(userId, callback) {
    const channel = supabase
      ?.channel('friendships_realtime')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'friendships',
          filter: `requester_id=eq.${userId}`
        },
        callback
      )
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'friendships',
          filter: `receiver_id=eq.${userId}`
        },
        callback
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to squad member updates
   * Listens to all squad_members changes for squads the user is in
   */
  subscribeToSquadMemberUpdates(callback) {
    const channel = supabase
      ?.channel('squad_members_realtime')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'squad_members'
        },
        callback
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to squad invitation updates for a specific user
   */
  subscribeToSquadInvitationUpdates(userId, callback) {
    const channel = supabase
      ?.channel('squad_invitations_realtime')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'squad_invitations',
          filter: `invited_user_id=eq.${userId}`
        },
        callback
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to incoming duel invites (new pending duels where user is the opponent)
   */
  subscribeToDuelInvites(userId, callback) {
    const channel = supabase
      ?.channel(`duel_invites_${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'duels',
          filter: `opponent_id=eq.${userId}`
        },
        callback
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to leaderboard changes — user_profiles updates for total_points or current_streak
   * Fires whenever any friend's profile is updated (points, streak, duels_won)
   */
  subscribeToLeaderboardChanges(callback) {
    const channel = supabase
      ?.channel('leaderboard_realtime')?.on('postgres_changes',
        {
          event: 'UPDATE',schema: 'public',table: 'user_profiles'
        },
        callback
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to new friend workout activity (workout_logs inserts from any user)
   * Social+ page filters to friends client-side after refetch
   */
  subscribeToFriendWorkoutActivity(callback) {
    const channel = supabase
      ?.channel('friend_workout_activity_realtime')?.on('postgres_changes',
        {
          event: 'INSERT',schema: 'public',table: 'workout_logs'
        },
        callback
      )
      ?.subscribe();

    return channel;
  }
};

/**
 * Service for managing duels (social betting system)
 */
export const duelService = {
  /**
   * Get all duels for a user (as creator or opponent)
   */
  async getUserDuels(userId, status = null) {
    let query = supabase
      ?.from('duels')
      ?.select(`
        *,
        creator:user_profiles!duels_creator_id_fkey(id, full_name, avatar_url),
        opponent:user_profiles!duels_opponent_id_fkey(id, full_name, avatar_url),
        stake:stakes(id, title, description, severity, icon)
      `)
      ?.or(`creator_id.eq.${userId},opponent_id.eq.${userId}`)
      ?.order('created_at', { ascending: false });

    if (status) {
      query = query?.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Get active duels for dashboard card
   */
  async getActiveDuels(userId) {
    const { data, error } = await supabase
      ?.from('duels')
      ?.select(`
        *,
        creator:user_profiles!duels_creator_id_fkey(id, full_name, avatar_url),
        opponent:user_profiles!duels_opponent_id_fkey(id, full_name, avatar_url),
        stake:stakes(id, title, description, severity, icon)
      `)
      ?.or(`creator_id.eq.${userId},opponent_id.eq.${userId}`)
      ?.eq('status', 'active')
      ?.order('end_date', { ascending: true })
      ?.limit(3);

    if (error) throw error;
    return data || [];
  },

  /**
   * Create a new duel
   */
  async createDuel(creatorId, duelData) {
    const startDate = new Date();
    const endDate = new Date();
    endDate?.setDate(endDate?.getDate() + (duelData?.durationDays || 7));

    const { data, error } = await supabase
      ?.from('duels')
      ?.insert({
        creator_id: creatorId,
        opponent_id: duelData?.opponentId,
        title: duelData?.title,
        description: duelData?.description,
        metric: duelData?.metric,
        target_value: duelData?.targetValue,
        duration_days: duelData?.durationDays,
        stake_id: duelData?.stakeId,
        verification_mode: duelData?.verificationMode || 'auto',
        status: 'pending',
        is_custom: duelData?.isCustom || false,
        custom_rules: duelData?.customRules || null,
        custom_reward: duelData?.customReward || null,
        custom_consequence: duelData?.customConsequence || null,
        verification_timer_minutes: duelData?.verificationTimerMinutes || 2,
        require_dual_camera: duelData?.requireDualCamera || false
      })
      ?.select(`
        *,
        creator:user_profiles!duels_creator_id_fkey(id, full_name, avatar_url),
        opponent:user_profiles!duels_opponent_id_fkey(id, full_name, avatar_url),
        stake:stakes(id, title, description, severity, icon)
      `)
      ?.single();

    if (error) throw error;

    // Send duel invite email to opponent
    // Remove this block - service not imported/defined
    // if (data?.opponent_id) {
    //   socialEmailService?.sendDuelInviteEmail(data?.opponent_id, {
    //     creatorId: creatorId,
    //     title: duelData?.title,
    //     description: duelData?.description,
    //     metric: duelData?.metric,
    //     targetValue: duelData?.targetValue,
    //     durationDays: duelData?.durationDays,
    //     stakeName: data?.stake?.title || 'Bragging rights'
    //   })?.catch(err => console.error('Failed to send duel invite email:', err));
    // }

    return data;
  },

  /**
   * Accept a duel invitation
   */
  async acceptDuel(duelId, userId) {
    const startDate = new Date();
    const endDate = new Date();
    
    // Get duel to calculate end date
    const { data: duel } = await supabase
      ?.from('duels')
      ?.select('duration_days')
      ?.eq('id', duelId)
      ?.single();
    
    if (duel) {
      endDate?.setDate(endDate?.getDate() + duel?.duration_days);
    }

    const { data, error } = await supabase
      ?.from('duels')
      ?.update({
        status: 'active',
        start_date: startDate?.toISOString()?.split('T')?.[0],
        end_date: endDate?.toISOString()?.split('T')?.[0]
      })
      ?.eq('id', duelId)
      ?.eq('opponent_id', userId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Reject/cancel a duel
   */
  async cancelDuel(duelId, userId) {
    const { data, error } = await supabase
      ?.from('duels')
      ?.update({ status: 'cancelled' })
      ?.eq('id', duelId)
      ?.or(`creator_id.eq.${userId},opponent_id.eq.${userId}`)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Update duel progress (auto-verification from tracking logs)
   */
  async updateDuelProgress(duelId, userId, progress) {
    const { data: duel } = await supabase
      ?.from('duels')
      ?.select('creator_id, opponent_id, target_value')
      ?.eq('id', duelId)
      ?.single();

    if (!duel) throw new Error('Duel not found');

    const isCreator = duel?.creator_id === userId;
    const progressField = isCreator ? 'creator_progress' : 'opponent_progress';
    
    const { data, error } = await supabase
      ?.from('duels')
      ?.update({ [progressField]: progress })
      ?.eq('id', duelId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Complete a duel and determine winner
   */
  async completeDuel(duelId) {
    const { data: duel } = await supabase
      ?.from('duels')
      ?.select('*')
      ?.eq('id', duelId)
      ?.single();

    if (!duel) throw new Error('Duel not found');

    let winnerId = null;
    
    // Determine winner based on progress
    if (duel?.creator_progress >= duel?.target_value && duel?.opponent_progress < duel?.target_value) {
      winnerId = duel?.creator_id;
    } else if (duel?.opponent_progress >= duel?.target_value && duel?.creator_progress < duel?.target_value) {
      winnerId = duel?.opponent_id;
    } else if (duel?.creator_progress >= duel?.target_value && duel?.opponent_progress >= duel?.target_value) {
      // Both completed - higher progress wins
      winnerId = duel?.creator_progress > duel?.opponent_progress ? duel?.creator_id : duel?.opponent_id;
    }

    const { data, error } = await supabase
      ?.from('duels')
      ?.update({
        status: 'completed',
        winner_id: winnerId
      })
      ?.eq('id', duelId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Get duel proofs for a specific duel
   */
  async getDuelProofs(duelId) {
    const { data, error } = await supabase
      ?.from('duel_proofs')
      ?.select(`
        *,
        user:user_profiles(id, full_name, avatar_url)
      `)
      ?.eq('duel_id', duelId)
      ?.order('proof_date', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Create a duel proof with photo
   */
  async createDuelProof(duelId, userId, proofData) {
    const { data, error } = await supabase
      ?.from('duel_proofs')
      ?.insert({
        duel_id: duelId,
        user_id: userId,
        proof_date: proofData?.proofDate || new Date()?.toISOString()?.split('T')?.[0],
        proof_value: proofData?.proofValue || 1,
        proof_type: proofData?.proofType || 'photo',
        photo_url: proofData?.photoUrl,
        auto_verified: proofData?.autoVerified || false,
        notes: proofData?.notes
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  }
};

/**
 * Service for managing stakes
 */
export const stakeService = {
  /**
   * Get all active stakes
   */
  async getStakes(severity = null) {
    let query = supabase
      ?.from('stakes')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('severity', { ascending: true });

    if (severity) {
      query = query?.eq('severity', severity);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Get stakes by category
   */
  async getStakesByCategory(category) {
    const { data, error } = await supabase
      ?.from('stakes')
      ?.select('*')
      ?.eq('is_active', true)
      ?.eq('category', category)
      ?.order('severity', { ascending: true });

    if (error) throw error;
    return data || [];
  }
};

/**
 * Service for managing challenge templates
 */
export const challengeTemplateService = {
  /**
   * Get all active challenge templates
   */
  async getTemplates() {
    const { data, error } = await supabase
      ?.from('challenge_templates')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('metric', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Get template by metric
   */
  async getTemplateByMetric(metric) {
    const { data, error } = await supabase
      ?.from('challenge_templates')
      ?.select('*')
      ?.eq('metric', metric)
      ?.eq('is_active', true)
      ?.single();

    if (error) throw error;
    return data;
  }
};

/**
 * Service for real-time duel updates
 */
export const duelRealtimeService = {
  /**
   * Subscribe to duel changes for a user
   */
  subscribeToDuels(userId, callback) {
    const channel = supabase
      ?.channel('duels_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duels',
          filter: `creator_id=eq.${userId}`
        },
        (payload) => {
          callback(payload);
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duels',
          filter: `opponent_id=eq.${userId}`
        },
        (payload) => {
          callback(payload);
        }
      )
      ?.subscribe();

    return channel;
  }
};

/**
 * Service for managing duel comments and reactions
 */
export const duelActivityService = {
  /**
   * Get comments for a duel
   */
  async getDuelComments(duelId) {
    const { data, error } = await supabase
      ?.from('duel_comments')
      ?.select(`
        *,
        user:user_profiles(id, full_name, avatar_url)
      `)
      ?.eq('duel_id', duelId)
      ?.order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  /**
   * Create a comment on a duel
   */
  async createComment(duelId, userId, commentText) {
    const { data, error } = await supabase
      ?.from('duel_comments')
      ?.insert({
        duel_id: duelId,
        user_id: userId,
        comment_text: commentText
      })
      ?.select(`
        *,
        user:user_profiles(id, full_name, avatar_url)
      `)
      ?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete a comment
   */
  async deleteComment(commentId) {
    const { error } = await supabase
      ?.from('duel_comments')
      ?.delete()
      ?.eq('id', commentId);

    if (error) throw error;
  },

  /**
   * Get reactions for a duel
   */
  async getDuelReactions(duelId) {
    const { data, error } = await supabase
      ?.from('duel_reactions')
      ?.select(`
        *,
        user:user_profiles(id, full_name, avatar_url)
      `)
      ?.eq('duel_id', duelId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Add a reaction to a duel or proof
   */
  async addReaction(duelId, userId, reactionType, proofId = null) {
    const { data, error } = await supabase
      ?.from('duel_reactions')
      ?.insert({
        duel_id: duelId,
        user_id: userId,
        reaction_type: reactionType,
        proof_id: proofId
      })
      ?.select(`
        *,
        user:user_profiles(id, full_name, avatar_url)
      `)
      ?.single();

    if (error) {
      // If duplicate, remove the reaction instead (toggle behavior)
      if (error?.code === '23505') {
        await supabase
          ?.from('duel_reactions')
          ?.delete()
          ?.eq('duel_id', duelId)
          ?.eq('user_id', userId)
          ?.eq('reaction_type', reactionType)
          ?.eq('proof_id', proofId);
        return null;
      }
      throw error;
    }
    return data;
  },

  /**
   * Get user's duel stats (W/L record)
   */
  async getUserDuelStats(userId) {
    const { data, error } = await supabase
      ?.from('user_profiles')?.select('duels_won, duels_lost, duels_completed')?.eq('id', userId)
      ?.single();

    if (error) throw error;
    return data || { duels_won: 0, duels_lost: 0, duels_completed: 0 };
  }
};

export default {
  challengeService,
  friendshipService,
  milestoneService,
  achievementService,
  socialRealtimeService,
  duelService,
  stakeService,
  challengeTemplateService,
  squadService,
  duelRealtimeService,
  duelActivityService
};